import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics } from '@/mocks/comics';

function formatViews(views: number): string {
  if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return views.toString();
}

const categoryNames: Record<string, string> = {
  romance: '恋爱',
  action: '动作',
  fantasy: '奇幻',
  drama: '剧情',
  thriller: '悬疑',
  comedy: '搞笑',
  horror: '惊悚',
  daily: '日常',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <div className="pt-24 pb-20 px-4 text-center">
        <i className="ri-error-warning-line text-5xl text-foreground-300"></i>
        <h1 className="mt-4 text-xl font-semibold text-foreground-950">漫画未找到</h1>
        <p className="mt-2 text-foreground-500">抱歉，您要找的漫画不存在或已被移除。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-home-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 6);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: `${categoryNames[comic.category] || comic.category}漫画`, to: `/category/${comic.category}` },
    { label: comic.title },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画 - 基德漫画网`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2025-01-01',
    dateModified: comic.updateDate,
    publisher: {
      '@type': 'Organization',
      name: '基德漫画网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://jidemanhua.com/article/${comic.id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>

      <div className="pt-20 pb-8 md:pb-10 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-6 flex flex-col lg:flex-row gap-8">
            <div className="flex-shrink-0 w-full lg:w-[300px]">
              <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={`${comic.title} - 韩国漫画 - 基德漫画网`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-sm font-medium">
                  <i className="ri-user-line text-sm"></i>
                  {comic.author}
                </span>
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                  comic.status === '已完结' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'
                }`}>
                  <i className={comic.status === '已完结' ? 'ri-check-double-line' : 'ri-loader-4-line text-sm'}></i>
                  {comic.status}
                </span>
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-sm">
                  <i className="ri-eye-line text-sm"></i>
                  {formatViews(comic.views)} 阅读
                </span>
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-background-100 text-accent-600 text-sm">
                  <i className="ri-star-fill text-sm"></i>
                  {comic.rating} 分
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-4">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6 p-4 rounded-lg bg-background-100">
                <h3 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="mt-5 flex items-center gap-3">
                <Link
                  to={`/category/${comic.category}`}
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  更多{categoryNames[comic.category] || comic.category}漫画 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-10">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              章节列表
              <span className="text-sm font-normal text-foreground-500 ml-2">共{comic.chapters}话</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {comic.chapters_list.map((ch) => (
                <div
                  key={ch.num}
                  className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer group"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {ch.title}
                    </p>
                    <p className="text-xs text-foreground-400 mt-0.5">{ch.date}</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-400 flex-shrink-0 ml-2"></i>
                </div>
              ))}
            </div>
            {comic.chapters > 7 && (
              <div className="mt-4 text-center">
                <button className="inline-flex items-center gap-1.5 px-6 py-2.5 bg-background-100 text-foreground-700 text-sm font-medium rounded-full hover:bg-background-200 transition-colors cursor-pointer">
                  查看更多章节
                  <i className="ri-arrow-down-s-line"></i>
                </button>
              </div>
            )}
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-12">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-product-shop="">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/article/${rc.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 mb-3">
                      <img
                        src={rc.cover}
                        alt={rc.title}
                        title={`${rc.title} - 韩国漫画 - 基德漫画网`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500/90 text-background-50 text-xs font-semibold">
                          {rc.rating}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {rc.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}